<template>
    <div v-if="job" class="JobViewLayoutLeft space-y-12 lg:space-y-18">
        <div v-if="job.image">
            <img
                loading="lazy"
                :src="job.image"
                class="max-h-[460px] w-full rounded-lg object-contain"
            />
        </div>

        <section v-if="customArticleContent" class="job-content-section">
            <div class="job-prose wysiwyg-enhancement" v-html="customArticleContent" />
        </section>

        <section v-if="!customArticleContent && job.challenge" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobChallenge', job.lang || 'sk') }}
            </CHeading>

            <div class="job-prose wysiwyg-enhancement" v-html="job.challenge" />
        </section>

        <section v-if="!customArticleContent && job.desc" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobDescription', job.lang || 'sk') }}
            </CHeading>

            <div class="job-prose wysiwyg-enhancement" v-html="job.desc" />
        </section>

        <section v-if="!customArticleContent && job.benefits" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobBenefits', job.lang || 'sk') }}
            </CHeading>

            <div class="job-prose wysiwyg-enhancement" v-html="job.benefits" />
        </section>

        <section v-if="job.languages?.length" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobLanguages.title', job.lang || 'sk') }}
            </CHeading>

            <div class="job-pill-grid">
                <div v-for="language in job.languages" :key="language.name" class="job-pill">
                    <span class="job-pill__name">{{ language.name }}</span>
                    <span class="job-pill__level">
                        {{ $t(`jobView.jobLanguages.type.${language.level}`, job.lang || 'sk') }}
                    </span>
                </div>
            </div>
        </section>

        <section v-if="job.skills?.length" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobSkills.title', job.lang || 'sk') }}
            </CHeading>

            <div class="job-pill-grid">
                <div v-for="skill in job.skills" :key="skill.name" class="job-pill">
                    <span class="job-pill__name">{{ skill.name }}</span>
                    <span class="job-pill__level">
                        {{ $t(`jobView.jobSkills.type.${skill.level}`, job.lang || 'sk') }}
                    </span>
                </div>
            </div>
        </section>

        <section v-if="preferencesFormattedList.length" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobPreferences', job.lang || 'sk') }}
            </CHeading>

            <ul class="list-disc pl-5">
                <li v-for="preference in preferencesFormattedList" :key="preference">{{ preference }}</li>
            </ul>
        </section>

        <section v-if="job.additionalInfo" class="job-content-section">
            <CHeading :variant="2">
                {{ $t('jobView.jobAdditionalInfos', job.lang || 'sk') }}
            </CHeading>

            <div class="job-prose wysiwyg-enhancement" v-html="job.additionalInfo" />
        </section>
    </div>
</template>

<script setup lang="ts">
import { valueLookup } from '#shared/types/dictionary'
import { clarity, clients, creativity, multitasking, teamwork } from '#shared/types/education'
import { JobContentMode, type Job } from '#shared/types/Job'

interface Props {
    job?: Job
}

const props = defineProps<Props>()

const customArticleContent = computed(() => {
    if (props.job?.contentMode !== JobContentMode.CUSTOM) return ''
    return props.job.content || ''
})

const preferencesFormattedList = computed(() => {
    if (!props.job) return []
    
    const preferences = [
        valueLookup(props.job.clients || '', clients),
        valueLookup(props.job.teamwork || '', teamwork),
        valueLookup(props.job.multitasking || '', multitasking),
        valueLookup(props.job.clarity || '', clarity),
        valueLookup(props.job.creativity || '', creativity)
    ]

    return preferences.filter(Boolean)
})
</script>

<style scoped>
.job-content-section {
    max-width: 760px;
}

.job-content-section :deep(h2) {
    margin-bottom: 1.25rem;
}

.job-prose {
    color: #1d191f;
    font-size: 1.0625rem;
    line-height: 1.8;
}

.job-prose :deep(.wysiwyg-text) {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
}

.job-prose :deep(p) {
    margin: 0;
}

.job-prose :deep(ul),
.job-prose :deep(ol) {
    margin: 0.25rem 0 0.25rem 0;
    padding-left: 1.4rem;
}

.job-prose :deep(li) {
    margin-top: 0.45rem;
    padding-left: 0.2rem;
}

.job-prose :deep(li::marker) {
    color: #047274;
}

.job-prose :deep(strong) {
    color: #111014;
    font-weight: 700;
}

.job-prose :deep(blockquote) {
    border-left: 3px solid #047274;
    color: #4d4552;
    font-style: normal;
    margin: 0.5rem 0;
    padding-left: 1rem;
}

.job-prose :deep(figure),
.job-prose :deep(img),
.job-prose :deep(.youtube-embed) {
    border-radius: 0.5rem;
    margin: 1.25rem 0;
    overflow: hidden;
}

.job-pill-grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    max-width: 760px;
}

.job-pill {
    align-items: flex-start;
    background: #f6f7f7;
    border: 1px solid #e0e0e4;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.75rem 0.875rem;
}

.job-pill__name {
    color: #1d191f;
    font-weight: 650;
}

.job-pill__level {
    color: #6c5d73;
    font-size: 0.925rem;
    line-height: 1.35;
}

/* Neutral surfaces follow the selected theme; brand colors and images retain their colors. */
[data-theme="dark"] .job-prose {
    color: var(--color-txt-body)
}
[data-theme="dark"] .job-prose :deep(strong) {
    color: var(--color-txt-headings)
}
/* Editor highlights get a translucent dark background but keep the browser's black mark text */
[data-theme="dark"] .job-prose :deep(mark) {
    color: inherit
}
[data-theme="dark"] .job-prose :deep(blockquote) {
    border-left-color: var(--color-primary-500);
    color: var(--color-txt-body)
}
[data-theme="dark"] .job-pill {
    background: var(--color-surface-card);
    border-color: var(--color-border)
}
[data-theme="dark"] .job-pill__name {
    color: var(--color-txt-headings)
}
[data-theme="dark"] .job-pill__level {
    color: var(--color-txt-caption)
}
</style>
